import { getSupabaseClient } from '@/integrations/supabase/client';
import type { Ingredient, IngredientCategory, IngredientUnit } from '@/types';
import { roundIngredientStock } from '@/utils/ingredientStock';
import { skuPayload } from '@/utils/sku';

function mapRow(row: Record<string, unknown>): Ingredient {
  const providers = row.providers as Record<string, unknown> | null | undefined;
  return {
    id: row.id as string,
    name: row.name as string,
    category: row.category as IngredientCategory,
    unit: row.unit as IngredientUnit,
    cost: Number(row.cost ?? 0),
    stock: Number(row.stock ?? 0),
    minimumStockLevel: row.minimum_stock_level != null ? Number(row.minimum_stock_level) : undefined,
    notes: (row.notes as string) || undefined,
    organizationId: row.organization_id as string,
    providerId: (row.provider_id as string) || undefined,
    providerCode: (row.provider_code as string) || undefined,
    sku: (row.sku as string) || undefined,
    avoidTracking: Boolean(row.avoid_tracking),
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    provider: providers
      ? {
          id: providers.id as string,
          name: providers.name as string,
          phone: (providers.phone as string) || undefined,
          bankAccountNumber: (providers.bank_account_number as string) || undefined,
          bank: (providers.bank as string) || undefined,
          email: (providers.email as string) || undefined,
          notes: (providers.notes as string) || undefined,
          organizationId: providers.organization_id as string,
          createdAt: new Date(providers.created_at as string),
          updatedAt: new Date(providers.updated_at as string),
        }
      : undefined,
  };
}

export type IngredientInput = Omit<
  Ingredient,
  'id' | 'organizationId' | 'provider' | 'createdAt' | 'updatedAt'
>;

export const ingredientsService = {
  async getIngredients(organizationId: string): Promise<Ingredient[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ingredients')
      .select(
        `
        *,
        providers (
          id,
          name,
          phone,
          bank_account_number,
          bank,
          email,
          notes,
          organization_id,
          created_at,
          updated_at
        )
      `
      )
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;
    return (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
  },

  async createIngredient(
    input: IngredientInput,
    organizationId: string
  ): Promise<Ingredient> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ingredients')
      .insert([
        {
          name: input.name,
          category: input.category,
          unit: input.unit,
          cost: input.cost,
          stock: input.stock ?? 0,
          minimum_stock_level: input.minimumStockLevel ?? null,
          provider_id: input.providerId ?? null,
          provider_code: input.providerCode ?? null,
          ...skuPayload(input.sku),
          notes: input.notes ?? null,
          organization_id: organizationId,
          avoid_tracking: input.avoidTracking ?? false,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return mapRow(data as Record<string, unknown>);
  },

  async updateIngredient(ingredient: Ingredient): Promise<Ingredient> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ingredients')
      .update({
        name: ingredient.name,
        category: ingredient.category,
        unit: ingredient.unit,
        cost: ingredient.cost,
        stock: ingredient.stock,
        minimum_stock_level: ingredient.minimumStockLevel ?? null,
        provider_id: ingredient.providerId ?? null,
        provider_code: ingredient.providerCode ?? null,
        ...skuPayload(ingredient.sku),
        notes: ingredient.notes ?? null,
        avoid_tracking: ingredient.avoidTracking,
        updated_at: new Date().toISOString(),
      })
      .eq('id', ingredient.id)
      .eq('organization_id', ingredient.organizationId)
      .select()
      .single();

    if (error) throw error;
    return mapRow(data as Record<string, unknown>);
  },

  async deleteIngredient(ingredientId: string, organizationId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('ingredients')
      .delete()
      .eq('id', ingredientId)
      .eq('organization_id', organizationId);
    if (error) throw error;
  },

  async getIngredientStock(ingredientId: string): Promise<number> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ingredients')
      .select('stock')
      .eq('id', ingredientId)
      .single();

    if (error) throw error;
    return Number(data.stock);
  },

  async updateIngredientStock(ingredientId: string, newStock: number): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('ingredients')
      .update({
        stock: roundIngredientStock(newStock >= 0 ? newStock : 0),
        updated_at: new Date().toISOString(),
      })
      .eq('id', ingredientId);

    if (error) throw error;
  },

  async updateIngredientStockByDelta(ingredientId: string, deltaQuantity: number): Promise<number> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ingredients')
      .select('stock, avoid_tracking')
      .eq('id', ingredientId)
      .single();

    if (error) throw error;
    if (data.avoid_tracking) {
      return Number(data.stock);
    }
    const currentStock = Number(data.stock);
    const next = roundIngredientStock(Math.max(0, currentStock + deltaQuantity));
    await this.updateIngredientStock(ingredientId, next);
    return next;
  },
};
